import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MouseEvent, KeyboardEvent, useRef, useEffect, memo } from "react";
import { Image } from "src/types/storefront";
import Slider from "./Slider";

type Props = {
  activeImage: number;
  images: Image[];
  setFullscreen(visible: boolean): void;
};

function SliderFullscreen({ images, activeImage, setFullscreen }: Props) {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Trap focus
  const handleKeyDown = (
    event: KeyboardEvent,
    firstInteractive: HTMLElement | null,
    lastInteractive: HTMLElement | null
  ) => {
    const { target, key, shiftKey } = event;

    if (key === "Tab" && shiftKey && target === firstInteractive) {
      event.preventDefault();
      lastInteractive?.focus();
    }

    if (key === "Tab" && !shiftKey && target === lastInteractive) {
      event.preventDefault();
      firstInteractive?.focus();
    }

    if (key === "Escape") {
      setFullscreen(false);
    }
  };

  const handleSlideClick = (
    event: MouseEvent<HTMLDivElement>,
    index: number
  ): void => {
    const target = event.target as HTMLDivElement;
    if (target.nodeName !== "IMG") {
      setFullscreen(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    sliderRef.current?.focus();

    return () => {
      document.body.style.overflow = "initial";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center"
      tabIndex={0}
      ref={sliderRef}
      aria-label="Fullscreen image mode. Press Escape to close"
      onKeyDown={(event) => {
        handleKeyDown(event, sliderRef.current, buttonRef.current);
      }}
    >
      <div
        onClick={() => setFullscreen(false)}
        className="absolute inset-0 bg-gray-900 opacity-90"
      />

      <div className="w-full">
        <Slider
          images={images}
          onSlideClick={handleSlideClick}
          activeImage={activeImage}
          imageClassName="m-auto w-auto h-[95vh]"
        />
      </div>

      <button
        ref={buttonRef}
        className="absolute right-4 top-4 rounded-full bg-white p-1 opacity-80 hover:opacity-100 focus:opacity-100"
        type="button"
        aria-label="Close fullscreen mode"
        onClick={() => setFullscreen(false)}
      >
        <XMarkIcon className="h-8 w-8" />
      </button>
    </div>
  );
}

export default memo(SliderFullscreen);
